import { createFileRoute, useNavigate } from '@tanstack/solid-router';
import { For, Match, Show, Switch, createMemo, onMount } from 'solid-js';

import Header from '~/components/layout/Header';
import { findBySlug, getQuestions, getSlug } from '~/lib/config/questions';
import { listRetailers } from '~/lib/config/retailers';
import { BUILDING_TYPE } from '~/lib/graphql/types';
import { trackAnsweredQuestions, trackEvent } from '~/lib/helpers/analytics';
import { posthog } from '~/lib/helpers/posthog';
import { countryCode, locale } from '~/lib/i18n';
import { useCalculator } from '~/lib/store';
import type { Question, QuestionItem, QuoteInput } from '~/lib/store/types';

export const Route = createFileRoute('/$locale/question/$slug')({
    component: QuestionPage
});

function QuestionPage() {
    const [state, actions] = useCalculator();
    const navigate = useNavigate();
    const params = Route.useParams();

    const questions = createMemo(() =>
        getQuestions(
            countryCode(),
            state.quote.type ?? BUILDING_TYPE.LANDED_HOUSE
        )
    );

    onMount(() => posthog.capture('$pageview'));

    const found = createMemo(() => findBySlug(questions(), params().slug));
    const currentQuestion = createMemo(() => found()?.question);
    const currentIndex = createMemo(() => found()?.index ?? 0);
    const totalSteps = createMemo(() => questions().length);
    const progress = createMemo(
        () => ((currentIndex() + 1) / totalSteps()) * 100
    );

    function handleAnswer(input: keyof QuoteInput, value: string) {
        actions.setQuote(input as keyof typeof state.quote, value as never);
    }

    /** Persist default values for any unanswered fields in the current question. */
    function persistDefaults() {
        const q = currentQuestion();
        if (!q) return;

        if (q.type === 'grouped' && 'groupedQuestions' in q) {
            q.groupedQuestions
                .filter(
                    (sub) => sub.value != null && state.quote[sub.input] == null
                )
                .forEach((sub) =>
                    actions.setQuote(
                        sub.input as keyof typeof state.quote,
                        sub.value as never
                    )
                );
        } else if (q.type !== 'grouped') {
            const single = q as Question;
            if (single.value != null && state.quote[single.input] == null) {
                actions.setQuote(
                    single.input as keyof typeof state.quote,
                    single.value as never
                );
            }
        }
    }

    function handleNext() {
        persistDefaults();
        trackEvent(`Step: ${params().slug}`, {
            quote: state.quote,
            set: state.set,
            direction: 'continue',
            utm: state.utm
        });
        const idx = currentIndex();
        if (idx < totalSteps() - 1) {
            const nextSlug = getSlug(questions()[idx + 1]);
            navigate({
                to: '/$locale/question/$slug',
                params: { slug: nextSlug },
                search: (prev) => prev
            });
        } else {
            trackAnsweredQuestions(state.quote.type ?? null);
            navigate({ to: '/$locale/map', search: (prev) => prev });
        }
    }

    function handleBack() {
        trackEvent(`Step: ${params().slug}`, {
            quote: state.quote,
            set: state.set,
            direction: 'back',
            utm: state.utm
        });
        const idx = currentIndex();
        if (idx > 0) {
            const prevSlug = getSlug(questions()[idx - 1]);
            navigate({
                to: '/$locale/question/$slug',
                params: { slug: prevSlug },
                search: (prev) => prev
            });
        } else {
            navigate({ to: '/$locale', search: (prev) => prev });
        }
    }

    return (
        <div class="min-h-screen bg-white">
            <Header />

            {/* Desktop Stepper */}
            <div class="hidden md:flex w-full py-4 px-10 h-16 items-center border-b border-gray-100">
                <div class="w-1/4" />
                <div class="flex justify-center w-2/4">
                    <Stepper
                        current={currentIndex() + 1}
                        total={totalSteps()}
                    />
                </div>
                <div class="w-1/4" />
            </div>

            {/* Mobile progress */}
            <div class="md:hidden px-4 py-3 border-b border-gray-100">
                <div class="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                    <span>
                        {currentIndex() + 1} of {totalSteps()}
                    </span>
                    <span>{Math.round(progress())}%</span>
                </div>
                <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                        class="h-full bg-brand-blue rounded-full transition-all duration-300"
                        style={{ width: `${progress()}%` }}
                    />
                </div>
            </div>

            {/* Question Content */}
            <div class="max-w-xl mx-auto px-4 py-8 sm:py-12">
                <Show
                    when={currentQuestion()}
                    fallback={<p>Question not found</p>}>
                    {(question) => (
                        <QuestionRenderer
                            question={question()}
                            quote={state.quote}
                            onAnswer={handleAnswer}
                        />
                    )}
                </Show>

                {/* Navigation */}
                <div class="flex items-center justify-between mt-10 gap-4">
                    <button
                        type="button"
                        onClick={handleBack}
                        class="rounded-full border border-brand-blue bg-white px-8 py-3 text-sm font-semibold text-brand-blue hover:bg-brand-blue/5 transition-colors">
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={handleNext}
                        class="rounded-full bg-brand-yellow px-8 py-3 text-sm font-semibold text-brand-black hover:opacity-90 transition-opacity">
                        {currentIndex() < totalSteps() - 1
                            ? 'Next'
                            : 'Continue'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Stepper
// ---------------------------------------------------------------------------

function Stepper(props: { current: number; total: number }) {
    return (
        <div class="flex items-center gap-2">
            <For each={Array.from({ length: props.total }, (_, i) => i + 1)}>
                {(step) => (
                    <div class="flex items-center gap-2">
                        <div
                            class={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                                step < props.current
                                    ? 'bg-brand-blue text-white'
                                    : step === props.current
                                      ? 'border-2 border-brand-blue text-brand-blue'
                                      : 'bg-gray-100 text-gray-400'
                            }`}>
                            <Show when={step < props.current} fallback={step}>
                                <svg
                                    class="w-4 h-4"
                                    fill="currentColor"
                                    viewBox="0 0 20 20">
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </Show>
                        </div>
                        <Show when={step < props.total}>
                            <div
                                class={`w-6 h-0.5 ${step < props.current ? 'bg-brand-blue' : 'bg-gray-200'}`}
                            />
                        </Show>
                    </div>
                )}
            </For>
        </div>
    );
}

// ---------------------------------------------------------------------------
// QuestionRenderer
// ---------------------------------------------------------------------------

function QuestionRenderer(props: {
    question: QuestionItem;
    quote: QuoteInput;
    onAnswer: (input: keyof QuoteInput, value: string) => void;
}) {
    return (
        <Switch>
            <Match
                when={
                    props.question.type === 'grouped' &&
                    'groupedQuestions' in props.question
                }>
                <div class="space-y-8">
                    <For
                        each={
                            (
                                props.question as {
                                    type: 'grouped';
                                    groupedQuestions: Question[];
                                }
                            ).groupedQuestions
                        }>
                        {(q) => (
                            <SingleQuestion
                                question={q}
                                quote={props.quote}
                                onAnswer={props.onAnswer}
                            />
                        )}
                    </For>
                </div>
            </Match>
            <Match when={props.question.type !== 'grouped'}>
                <SingleQuestion
                    question={props.question as Question}
                    quote={props.quote}
                    onAnswer={props.onAnswer}
                />
            </Match>
        </Switch>
    );
}

// ---------------------------------------------------------------------------
// SingleQuestion
// ---------------------------------------------------------------------------

function SingleQuestion(props: {
    question: Question;
    quote: QuoteInput;
    onAnswer: (input: keyof QuoteInput, value: string) => void;
}) {
    const currentValue = createMemo(
        () =>
            (props.quote[props.question.input] as string) ??
            props.question.value ??
            ''
    );

    return (
        <div>
            <h2 class="text-xl sm:text-2xl font-heading font-bold text-brand-black mb-4">
                {props.question.label}
            </h2>

            <Show when={props.question.description}>
                <p class="text-sm text-gray-500 mb-4">
                    {props.question.description}
                </p>
            </Show>

            <Switch>
                {/* Select with nested questions (radio card layout) */}
                <Match
                    when={
                        props.question.type === 'select' &&
                        props.question.option?.some((o) => o.nestedQuestion)
                    }>
                    <div class="space-y-3">
                        <For each={props.question.option}>
                            {(option) => (
                                <div
                                    class={`rounded-xl border-2 p-4 cursor-pointer transition-colors ${
                                        currentValue() === option.value
                                            ? 'border-brand-blue bg-brand-blue/25'
                                            : 'border-brand-blue/25 bg-white hover:border-brand-blue/50'
                                    }`}
                                    onClick={() =>
                                        props.onAnswer(
                                            props.question.input,
                                            option.value ?? ''
                                        )
                                    }>
                                    <div class="flex items-center gap-3">
                                        <div
                                            class={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                                currentValue() === option.value
                                                    ? 'border-brand-blue'
                                                    : 'border-gray-300'
                                            }`}>
                                            <Show
                                                when={
                                                    currentValue() ===
                                                    option.value
                                                }>
                                                <div class="w-2.5 h-2.5 rounded-full bg-brand-blue" />
                                            </Show>
                                        </div>
                                        <span class="text-sm font-medium text-gray-700">
                                            {option.label}
                                        </span>
                                    </div>
                                    <Show
                                        when={
                                            currentValue() === option.value &&
                                            option.nestedQuestion
                                        }>
                                        <div
                                            class="mt-3 ml-8"
                                            onClick={(e) =>
                                                e.stopPropagation()
                                            }>
                                            <NestedInput
                                                question={
                                                    option.nestedQuestion!
                                                }
                                                quote={props.quote}
                                                onAnswer={props.onAnswer}
                                            />
                                        </div>
                                    </Show>
                                </div>
                            )}
                        </For>
                    </div>
                </Match>

                {/* Regular select (2-col with images, 1-col without) */}
                <Match
                    when={
                        props.question.type === 'select' &&
                        props.question.option
                    }>
                    <div
                        class={`grid gap-3 ${
                            props.question.option?.some((o) => o.asset)
                                ? 'grid-cols-1 sm:grid-cols-2'
                                : 'grid-cols-1'
                        }`}>
                        <For each={props.question.option}>
                            {(option) => (
                                <button
                                    type="button"
                                    class={`flex items-center gap-3 p-4 rounded-lg border-2 text-left transition-colors text-brand-blue ${
                                        currentValue() === option.value
                                            ? 'border-brand-blue bg-brand-blue/25'
                                            : 'border-brand-blue/25 bg-white hover:border-brand-blue/50'
                                    }`}
                                    onClick={() =>
                                        props.onAnswer(
                                            props.question.input,
                                            option.value ?? ''
                                        )
                                    }>
                                    <Show when={option.asset}>
                                        {(asset) => (
                                            <img
                                                src={asset().path}
                                                alt={option.label}
                                                width={asset().width}
                                                height={asset().height}
                                                class="shrink-0"
                                            />
                                        )}
                                    </Show>
                                    <span class="text-sm font-medium">
                                        {option.label}
                                    </span>
                                </button>
                            )}
                        </For>
                    </div>
                </Match>

                <Match when={props.question.type === 'currency'}>
                    <div>
                        <div class="relative">
                            <span class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                                {locale().currency}
                            </span>
                            <input
                                type="number"
                                value={currentValue()}
                                onInput={(e) =>
                                    props.onAnswer(
                                        props.question.input,
                                        e.currentTarget.value
                                    )
                                }
                                class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                                placeholder="Enter amount"
                            />
                        </div>
                        <Show when={props.question.billHintValues}>
                            <div class="flex flex-wrap gap-2 mt-3">
                                <For each={props.question.billHintValues}>
                                    {(hint) => (
                                        <button
                                            type="button"
                                            class={`px-3 py-1.5 rounded-full text-xs font-medium border text-brand-blue transition-colors ${
                                                currentValue() === String(hint)
                                                    ? 'border-brand-blue bg-brand-blue/25'
                                                    : 'border-brand-blue/25 bg-white hover:border-brand-blue/50'
                                            }`}
                                            onClick={() =>
                                                props.onAnswer(
                                                    props.question.input,
                                                    String(hint)
                                                )
                                            }>
                                            {locale().currency}
                                            {hint}
                                        </button>
                                    )}
                                </For>
                            </div>
                        </Show>
                    </div>
                </Match>

                <Match when={props.question.type === 'number'}>
                    <input
                        type="number"
                        value={currentValue()}
                        onInput={(e) =>
                            props.onAnswer(
                                props.question.input,
                                e.currentTarget.value
                            )
                        }
                        class="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                        placeholder="Enter a number"
                    />
                </Match>

                <Match when={props.question.type === 'dropdown'}>
                    {(() => {
                        const options = () => {
                            if (props.question.option)
                                return props.question.option;
                            if (props.question.input === 'retailer') {
                                return listRetailers.map((r) => ({
                                    label: r.label,
                                    value: r.value,
                                    type: 'option' as const,
                                    input: 'retailer' as const
                                }));
                            }
                            return [];
                        };

                        return (
                            <select
                                value={currentValue()}
                                onChange={(e) =>
                                    props.onAnswer(
                                        props.question.input,
                                        e.currentTarget.value
                                    )
                                }
                                class="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent">
                                <For each={options()}>
                                    {(opt) => (
                                        <option value={opt.value ?? ''}>
                                            {opt.label}
                                        </option>
                                    )}
                                </For>
                            </select>
                        );
                    })()}
                </Match>

                <Match when={props.question.type === 'text'}>
                    <input
                        type="text"
                        value={currentValue()}
                        onInput={(e) =>
                            props.onAnswer(
                                props.question.input,
                                e.currentTarget.value
                            )
                        }
                        class="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                        placeholder="Enter your answer"
                    />
                </Match>
            </Switch>
        </div>
    );
}

// ---------------------------------------------------------------------------
// NestedInput — renders a nested question inside a radio card
// ---------------------------------------------------------------------------

function NestedInput(props: {
    question: Question;
    quote: QuoteInput;
    onAnswer: (input: keyof QuoteInput, value: string) => void;
}) {
    const nestedValue = createMemo(
        () =>
            (props.quote[props.question.input] as string) ??
            props.question.value ??
            ''
    );

    return (
        <Switch>
            <Match when={props.question.type === 'currency'}>
                <div>
                    <Show when={props.question.label}>
                        <p class="text-sm text-gray-600 mb-2">
                            {props.question.label}
                        </p>
                    </Show>
                    <div class="relative">
                        <span class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                            {locale().currency}
                        </span>
                        <input
                            type="number"
                            value={nestedValue()}
                            onInput={(e) =>
                                props.onAnswer(
                                    props.question.input,
                                    e.currentTarget.value
                                )
                            }
                            class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                            placeholder="e.g., 300"
                        />
                    </div>
                    <Show when={props.question.billHintValues}>
                        <div class="flex flex-wrap gap-2 mt-3">
                            <For each={props.question.billHintValues}>
                                {(hint) => (
                                    <button
                                        type="button"
                                        class={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                                            nestedValue() === String(hint)
                                                ? 'border-brand-blue bg-blue-50 text-brand-blue'
                                                : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                        }`}
                                        onClick={() =>
                                            props.onAnswer(
                                                props.question.input,
                                                String(hint)
                                            )
                                        }>
                                        {locale().currency}
                                        {hint}
                                    </button>
                                )}
                            </For>
                        </div>
                    </Show>
                </div>
            </Match>

            <Match
                when={
                    props.question.type === 'choice' && props.question.option
                }>
                <div>
                    <Show when={props.question.label}>
                        <p class="text-sm text-gray-600 mb-2">
                            {props.question.label}
                        </p>
                    </Show>
                    <div class="space-y-2">
                        <For each={props.question.option}>
                            {(opt) => (
                                <button
                                    type="button"
                                    class={`w-full p-3 rounded-lg border-2 text-left text-brand-blue transition-colors ${
                                        nestedValue() === opt.value
                                            ? 'border-brand-blue bg-brand-blue/25'
                                            : 'border-brand-blue/25 bg-white hover:border-brand-blue/50'
                                    }`}
                                    onClick={() =>
                                        props.onAnswer(
                                            props.question.input,
                                            opt.value ?? ''
                                        )
                                    }>
                                    <span class="text-sm font-medium">
                                        {opt.label}
                                    </span>
                                    <Show when={opt.description}>
                                        <p class="text-xs text-brand-blue/70 mt-0.5">
                                            {opt.description}
                                        </p>
                                    </Show>
                                </button>
                            )}
                        </For>
                    </div>
                </div>
            </Match>
        </Switch>
    );
}
